import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../_shared/components/StatusBadge';
import { HealthIndicator } from '../../_shared/components/HealthIndicator';
import { formatGatewayDate, normalizeHealthState } from '../../_shared/utils/format';
import { useServiceDetailQuery } from '../hooks/useServices';

interface ServiceDetailDialogProps {
  serviceId: string;
  serviceName: string;
  onClose: () => void;
}

/**
 * 서비스 상세 read-only 다이얼로그 (#621).
 *
 * BE 에 create/update/delete 엔드포인트가 없으므로 편집 폼이 아닌 조회 전용 패널이다 —
 * 소속 타겟(+헬스) 목록과 적용 upstream 요약을 표시한다.
 */
export function ServiceDetailDialog({ serviceId, serviceName, onClose }: ServiceDetailDialogProps) {
  const { t } = useTranslation();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const { data, isLoading, isError, refetch } = useServiceDetailQuery(serviceId);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    const id = requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', handler);
      cancelAnimationFrame(id);
    };
  }, [onClose]);

  return (
    <div
      role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('gateway.services.detail.title', { name: serviceName })}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface-background)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-6)', minWidth: '520px', maxWidth: '720px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>
            {t('gateway.services.detail.title', { name: serviceName })}
          </h2>
          <button ref={closeBtnRef} type="button" onClick={onClose} style={closeBtnStyle} aria-label={t('gateway.shared.actions.close')}>
            &times;
          </button>
        </div>

        {isLoading && (
          <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} style={{ height: '36px', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-md)' }} />
            ))}
          </div>
        )}

        {isError && (
          <div role="alert" style={{ marginTop: 'var(--spacing-4)', padding: 'var(--spacing-4)', border: '1px solid var(--color-danger)', borderRadius: 'var(--radius-md)' }}>
            <p style={{ margin: 0, color: 'var(--color-danger)' }}>{t('gateway.services.detail.error')}</p>
            <button type="button" onClick={() => refetch()} style={{ marginTop: 'var(--spacing-2)', ...actionBtnStyle }}>
              {t('gateway.shared.actions.retry')}
            </button>
          </div>
        )}

        {data && (
          <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
            <section>
              <h3 style={sectionTitleStyle}>{t('gateway.services.detail.overview')}</h3>
              <dl style={dlStyle}>
                <dt style={dtStyle}>{t('gateway.services.columns.serviceKey')}</dt>
                <dd style={ddMonoStyle}>{data.serviceKey}</dd>
                <dt style={dtStyle}>{t('gateway.services.columns.description')}</dt>
                <dd style={ddStyle}>{data.description || '-'}</dd>
                <dt style={dtStyle}>{t('gateway.services.columns.status')}</dt>
                <dd style={ddStyle}>
                  <StatusBadge status={data.enabled ? 'enabled' : 'disabled'} size="sm" />
                </dd>
                <dt style={dtStyle}>{t('gateway.services.detail.updatedAt')}</dt>
                <dd style={ddStyle}>{formatGatewayDate(data.updatedAt)}</dd>
              </dl>
            </section>

            <section>
              <h3 style={sectionTitleStyle}>{t('gateway.services.detail.upstream')}</h3>
              {data.upstream ? (
                <dl style={dlStyle}>
                  <dt style={dtStyle}>{t('gateway.services.detail.upstreamName')}</dt>
                  <dd style={ddStyle}>{data.upstream.name}</dd>
                  <dt style={dtStyle}>{t('gateway.services.detail.algorithm')}</dt>
                  <dd style={ddMonoStyle}>{data.upstream.algorithm}</dd>
                  <dt style={dtStyle}>{t('gateway.services.detail.healthcheck')}</dt>
                  <dd style={ddStyle}>
                    {t('gateway.services.detail.healthcheckActive')}: {data.upstream.healthcheckActive ? 'O' : 'X'}
                    {' · '}
                    {t('gateway.services.detail.healthcheckPassive')}: {data.upstream.healthcheckPassive ? 'O' : 'X'}
                  </dd>
                </dl>
              ) : (
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  {t('gateway.services.detail.noUpstream')}
                </p>
              )}
            </section>

            <section>
              <h3 style={sectionTitleStyle}>
                {t('gateway.services.detail.targets', { count: data.targets.length })}
              </h3>
              {data.targets.length === 0 ? (
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  {t('gateway.services.detail.noTargets')}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('gateway.targets.columns.host')}</th>
                      <th style={thStyle}>{t('gateway.targets.columns.weight')}</th>
                      <th style={thStyle}>{t('gateway.targets.columns.source')}</th>
                      <th style={thStyle}>{t('gateway.targets.columns.health')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.targets.map((target) => (
                      <tr key={target.id}>
                        <td style={tdMonoStyle}>{target.host}:{target.port}</td>
                        <td style={tdStyle}>{target.weight}</td>
                        <td style={tdStyle}>{target.source}</td>
                        <td style={tdStyle}>
                          <HealthIndicator state={normalizeHealthState(target.healthStatus)} showLabel size="sm" latencyMs={target.lastLatencyMs ?? undefined} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-2)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const dlStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  rowGap: 'var(--spacing-2)',
  columnGap: 'var(--spacing-3)',
  margin: 0,
};

const dtStyle: React.CSSProperties = { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' };
const ddStyle: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-sm)' };
const ddMonoStyle: React.CSSProperties = { ...ddStyle, fontFamily: 'var(--font-family-mono)' };

const thStyle: React.CSSProperties = { textAlign: 'left', padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)', color: 'var(--color-text-secondary)' };
const tdStyle: React.CSSProperties = { padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)' };
const tdMonoStyle: React.CSSProperties = { ...tdStyle, fontFamily: 'var(--font-family-mono)' };

const closeBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: 'var(--font-size-xl, 24px)',
  lineHeight: 1,
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
};

const actionBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};
